// Server-authoritative preferences with instant local echo. The backend owns the
// truth (user_preferences row); the client applies every change immediately
// (optimistic) and persists via a debounced PUT. Last-write-wins (locked: fine
// for prefs). A failed save rolls back to the last server-confirmed state.
//
// This provider deliberately imports only the PURE theme module (no cad-react),
// so it stays in the main bundle without pulling konva. It themes the whole DOM
// chrome via the --cad-* CSS tokens; the on-canvas palette is themed by the lazy
// editor chunk (cad/canvasTheme.ts), driven off the same prefs.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import type { Preferences } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
  DEFAULT_PREFERENCES,
  applyPreferences,
  clearThemeHint,
  toPreferences,
  writeThemeHint,
} from './theme';

interface PreferencesState {
  prefs: Preferences; // never null — defaults until the server load resolves
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** Optimistically apply + debounced-persist a full Preferences object. */
  update: (next: Preferences) => void;
  /** Restore the shipped defaults (and persist them). */
  reset: () => void;
}

const PreferencesContext = createContext<PreferencesState | null>(null);
const SAVE_DEBOUNCE_MS = 500;

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const userId = me?.id ?? null;

  const [prefs, setPrefs] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The last server-confirmed state — the rollback target on a failed save.
  const lastSavedRef = useRef<Preferences>(DEFAULT_PREFERENCES);
  // Mirror of current prefs for the media-query listener (avoids re-subscribing).
  const prefsRef = useRef<Preferences>(prefs);
  prefsRef.current = prefs;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleSave = useCallback((next: Preferences) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const out = await api.putPreferences(next);
          lastSavedRef.current = toPreferences(out);
          setError(null);
        } catch {
          // Roll the visible state back to what the server last confirmed.
          setError('Could not save your settings — reverted to the last saved version.');
          const rollback = lastSavedRef.current;
          setPrefs(rollback);
          writeThemeHint(applyPreferences(rollback));
        } finally {
          setSaving(false);
        }
      })();
    }, SAVE_DEBOUNCE_MS);
  }, []);

  const update = useCallback(
    (next: Preferences) => {
      setPrefs(next);
      writeThemeHint(applyPreferences(next)); // optimistic, instant
      scheduleSave(next);
    },
    [scheduleSave],
  );

  const reset = useCallback(() => update(DEFAULT_PREFERENCES), [update]);

  // Load on auth; revert to defaults on logout.
  useEffect(() => {
    if (!userId) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      lastSavedRef.current = DEFAULT_PREFERENCES;
      setPrefs(DEFAULT_PREFERENCES);
      applyPreferences(DEFAULT_PREFERENCES);
      clearThemeHint();
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const out = await api.getPreferences();
        if (cancelled) return;
        const loaded = toPreferences(out);
        lastSavedRef.current = loaded;
        setPrefs(loaded);
        writeThemeHint(applyPreferences(loaded));
      } catch {
        // Theme is non-critical: fall back to defaults silently.
        if (!cancelled) {
          setPrefs(DEFAULT_PREFERENCES);
          applyPreferences(DEFAULT_PREFERENCES);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Follow the OS theme live while mode === 'system'.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (prefsRef.current.theme.mode === 'system') {
        writeThemeHint(applyPreferences(prefsRef.current));
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Flush any pending save timer on unmount.
  useEffect(() => () => void (saveTimer.current && clearTimeout(saveTimer.current)), []);

  const value = useMemo<PreferencesState>(
    () => ({ prefs, loading, saving, error, update, reset }),
    [prefs, loading, saving, error, update, reset],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesState {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within <PreferencesProvider>');
  return ctx;
}
